import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useCustomFields,
  useCustomFieldValues,
  useUpdateCustomFieldValue,
  useCostImpactConfig,
  useProject
} from '../../hooks/useQueries';
import { useFieldConfigs } from '../../hooks/useFieldConfigs';
import FieldTooltip from '../FieldTooltip';
import FieldRenderer from '../FieldRenderer';
import LoadingSpinner from '../Popup/LoadingSpinner';
import Toast from '../Popup/Toast';
import Select from 'react-select';
import './CustomFieldsRenderer.css';

const COST_IMPACT_FIELD_NAME = 'cost_impact';
const COST_IMPACT_LABELS = {
  1: 'Very Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Very High'
};

const formatCurrencyDisplay = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }

  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) {
    return '';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numericAmount);
};

const CostImpactDropdown = ({
  field,
  fieldConfig,
  isRequired,
  editable,
  isEditing,
  isSaving,
  scoreValue,
  onStartEdit,
  onCancelEdit,
  onSave,
  costImpactConfig,
  onShowModal
}) => {

  const handleChange = async (selectedOption) => {
    const newValue = selectedOption ? selectedOption.value : null;
    // Save as string to match option_value format in database
    await onSave(newValue || null, null);
  };

  // Create options for React Select in descending order (5, 4, 3, 2, 1)
  // This component should only be rendered when valid costImpactConfig exists
  const selectOptions = useMemo(() => {
    const getOptionLabel = (score) => {
      const threshold = costImpactConfig?.thresholds?.find(t => t.score === parseInt(score));
      if (threshold) {
        const minFormatted = formatCurrencyDisplay(threshold.min);
        const maxFormatted = threshold.max ? formatCurrencyDisplay(threshold.max) : 'Unlimited';
        return `${score} - ${threshold.label} (${minFormatted} - ${maxFormatted})`;
      }
      return `${score} - ${COST_IMPACT_LABELS[score] || `Level ${score}`}`;
    };
    
    return [5, 4, 3, 2, 1].map(score => ({
      value: score.toString(),
      label: getOptionLabel(score)
    }));
  }, [costImpactConfig]);

  const selectedOption = selectOptions.find(opt => opt.value === (scoreValue || '').toString()) || null;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      padding: '2px 10px',
      backgroundColor: '#f8f9fa'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '10px 12px'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6c757d'
    })
  };

  return (
    <div className="detail-field cost-impact-field">
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label style={{ flex: '0 0 auto' }}>
          <span className="field-label-text">
            {fieldConfig.label}
            {isRequired && <span className="required-indicator">*</span>}
            {fieldConfig.tooltip && <FieldTooltip text={fieldConfig.tooltip} />}
          </span>
        </label>
        <button
          type="button"
          className="cost-impact-info-btn"
          onClick={() => onShowModal && onShowModal()}
          title="View cost impact scale"
          style={{
            padding: '2px 4px',
            minWidth: 'auto',
            width: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 auto'
          }}
        >
          <i className="bi bi-info-square"></i>
        </button>
      </div>
      <div className="field-value-container">
        <Select
          value={selectedOption}
          onChange={handleChange}
          options={selectOptions}
          isDisabled={isSaving || !editable}
          isClearable
          placeholder="Select impact level..."
          className="inline-edit-select"
          styles={customStyles}
        />
      </div>
    </div>
  );
};

// Top-level modal component to render outside field container
const CostImpactModal = ({ showModal, onClose, costImpactConfig, formatCurrencyDisplay, COST_IMPACT_LABELS, projectId }) => {
  // Fetch project data for context
  const { data: project } = useProject(projectId, { enabled: !!projectId && showModal });
  
  if (!showModal) return null;
  
  return createPortal(
    <div 
      className="cost-impact-modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1051,
        backdropFilter: 'blur(4px)'
      }}
    >
      <div 
        className="cost-impact-modal" 
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          animation: 'modalSlideDown 0.3s ease-out'
        }}
      >
        <div 
          className="cost-impact-modal-header"
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#212529' }}>
              Cost Impact Scale
            </h3>
            {project && (
              <p style={{ 
                margin: '0.5rem 0 0 0', 
                fontSize: '0.95rem', 
                color: '#6c757d',
                fontWeight: '500'
              }}>
                Project: {project.name}
              </p>
            )}
          </div>
          <button 
            className="cost-impact-modal-close"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: '#6c757d',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div 
          className="cost-impact-modal-body"
          style={{
            padding: '1.5rem',
            overflowY: 'auto',
            maxHeight: 'calc(80vh - 200px)'
          }}
        >
          {costImpactConfig?.thresholds && costImpactConfig.thresholds.length > 0 ? (
            <div className="cost-impact-scale-list">
              {costImpactConfig.thresholds
                .sort((a, b) => b.score - a.score)
                .map((threshold) => {
                  const minFormatted = formatCurrencyDisplay(threshold.min);
                  const maxFormatted = threshold.max ? formatCurrencyDisplay(threshold.max) : 'Unlimited';
                  return (
                    <div key={threshold.score} className="cost-impact-scale-item">
                      <div className="scale-score">{threshold.score}</div>
                      <div className="scale-info">
                        <div className="scale-label">{threshold.label}</div>
                        <div className="scale-range">{minFormatted} - {maxFormatted}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div style={{
              padding: '2rem',
              textAlign: 'center',
              color: '#6c757d',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <i className="bi bi-exclamation-circle" style={{ fontSize: '2rem', color: '#ffc107', marginBottom: '1rem' }}></i>
              <p style={{ margin: 0, fontWeight: '500' }}>Cost impact scale not configured for this project.</p>
            </div>
          )}
          
          {/* Admin note */}
          <div 
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: '#6c757d'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <i className="bi bi-info-circle" style={{ color: 'var(--nsi-brand)', marginTop: '0.1rem' }}></i>
              <div>
                Cost impact scales can be configured per project in the Settings page under Project Configuration. Contact your system administrator to modify these ranges.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CustomFieldsRenderer = ({
  itemId,
  projectId = null,
  editable = true,
  onValuesChange = null,
  filterFields = null  // Array of field names to filter to, or null for all fields
}) => {
  const [editingFields, setEditingFields] = useState(new Set());
  const [savingFields, setSavingFields] = useState(new Set());
  const [showCostImpactModal, setShowCostImpactModal] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  const normalizedItemId = itemId !== null && itemId !== undefined ? itemId.toString() : null;

  // Pass projectId to ensure same query cache key as useCustomFields below
  const { fieldConfigs } = useFieldConfigs({ projectId });

  const {
    data: customFields = [],
    isLoading: fieldsLoading,
    error: fieldsError
  } = useCustomFields(projectId, true);

  const {
    data: customFieldValues = [],
    isLoading: valuesLoading,
    error: valuesError
  } = useCustomFieldValues(normalizedItemId);

  const {
    data: costImpactConfig,
    isLoading: costImpactConfigLoading,
    error: costImpactConfigError
  } = useCostImpactConfig(projectId, { enabled: !!projectId });

  const updateCustomFieldValue = useUpdateCustomFieldValue();

  const enrichedValues = useMemo(() => {
    const values = {};

    customFieldValues.forEach((cfv) => {
      const key = `custom_field_${cfv.custom_field_id}`;
      values[key] = cfv.value;
    });

    return values;
  }, [customFieldValues]);

  const handleFieldSave = async (fieldKey, value, metadata = null) => {
    const fieldId = fieldKey.replace('custom_field_', '');

    if (!normalizedItemId) {
      console.warn('CustomFieldsRenderer: Missing itemId, unable to save field value for', fieldKey);
      return;
    }

    setSavingFields((prev) => new Set([...prev, fieldKey]));

    try {
      await updateCustomFieldValue.mutateAsync({
        itemId: normalizedItemId,
        fieldId,
        value,
        metadata,
        costAmount: metadata?.cost_amount
      });

      setToast({ show: false, message: '', type: 'error' });

      if (onValuesChange) {
        onValuesChange({ ...enrichedValues, [fieldKey]: value });
      }
    } catch (error) {
      console.error('Error saving custom field value:', error);
      const isConflict = error?.status === 409 || error?.details?.code === 'ITEM_CONFLICT';
      setToast({
        show: true,
        message: isConflict
          ? (error?.message || 'This item was updated by someone else. Please review latest changes and try again.')
          : (error?.message || 'Failed to update custom field value.'),
        type: 'error'
      });
    } finally {
      setSavingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
      setEditingFields((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fieldKey);
        return newSet;
      });
    }
  };

  const handleStartEdit = (fieldKey) => {
    if (!editable) {
      return;
    }
    setEditingFields((prev) => new Set([...prev, fieldKey]));
  };

  const handleStopEdit = (fieldKey) => {
    setEditingFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldKey);
      return newSet;
    });
  };

  if (fieldsLoading || valuesLoading) {
    return <LoadingSpinner />;
  }

  if (fieldsError || valuesError) {
    return (
      <div className="custom-fields-error">
        <p>Error loading custom fields: {fieldsError?.message || valuesError?.message}</p>
      </div>
    );
  }

  if (customFields.length === 0) {
    return null;
  }

  return (
    <div className="custom-fields-renderer">
      <div className="custom-fields-header">
        <h3>Custom Fields</h3>
      </div>

      <div className="custom-fields-grid">
        {customFields
          .filter((field) => {
            // Apply field filtering if specified
            if (filterFields && Array.isArray(filterFields)) {
              return filterFields.includes(field.name);
            }
            return true;
          })
          .map((field) => {
          const fieldKey = `custom_field_${field.id}`;
          
          // Check for cost_impact field FIRST before checking fieldConfig
          // Cost impact is strictly project-dependent - no project = no field
          if (field.name === COST_IMPACT_FIELD_NAME) {
            const fieldConfig = fieldConfigs[fieldKey] || { label: field.label, type: 'select' };
            
            // Case 1: No project selected - show informative message
            if (!projectId) {
              return (
                <div key={fieldKey} className="detail-field cost-impact-field">
                  <label>
                    <span className="field-label-text">
                      {fieldConfig.label}
                      {field.is_required && <span className="required-indicator">*</span>}
                    </span>
                  </label>
                  <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    color: '#856404',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <i className="bi bi-info-circle" style={{ fontSize: '1.1rem' }}></i>
                    <span>Select a task order to configure cost impact</span>
                  </div>
                </div>
              );
            }
            
            // Case 2: Project exists but config is loading
            if (costImpactConfigLoading) {
              return (
                <div key={fieldKey} className="cost-impact-loading">
                  <LoadingSpinner />
                </div>
              );
            }
            
            // Case 3: Config fetch failed
            if (costImpactConfigError) {
              return (
                <div key={fieldKey} className="detail-field cost-impact-field">
                  <label>
                    <span className="field-label-text">
                      {fieldConfig.label}
                      {field.is_required && <span className="required-indicator">*</span>}
                    </span>
                  </label>
                  <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #dc3545',
                    borderRadius: '4px',
                    color: '#721c24',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.1rem' }}></i>
                    <span>Failed to load cost impact configuration for this project</span>
                  </div>
                </div>
              );
            }
            
            // Case 4: Config loaded but no thresholds configured
            if (!costImpactConfig?.thresholds || costImpactConfig.thresholds.length === 0) {
              return (
                <div key={fieldKey} className="detail-field cost-impact-field">
                  <label>
                    <span className="field-label-text">
                      {fieldConfig.label}
                      {field.is_required && <span className="required-indicator">*</span>}
                    </span>
                  </label>
                  <div style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: '#e7f3ff',
                    border: '1px solid #0dcaf0',
                    borderRadius: '4px',
                    color: '#055160',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem'
                  }}>
                    <i className="bi bi-info-circle" style={{ fontSize: '1.1rem' }}></i>
                    <span>Cost impact not configured for this project. Contact administrator.</span>
                  </div>
                </div>
              );
            }
            
            // Case 5: Valid config exists - render the dropdown
            return (
              <CostImpactDropdown
                key={fieldKey}
                field={field}
                fieldConfig={fieldConfig}
                isRequired={field.is_required}
                editable={editable}
                isSaving={savingFields.has(fieldKey)}
                scoreValue={enrichedValues[fieldKey]}
                onSave={(score, metadataPayload) => handleFieldSave(fieldKey, score, metadataPayload)}
                costImpactConfig={costImpactConfig}
                onShowModal={() => setShowCostImpactModal(true)}
              />
            );
          }

          // For all other fields, get the config and check if it exists
          const fieldConfig = fieldConfigs[fieldKey];
          
          if (!fieldConfig) {
            return null;
          }

          return (
            <FieldRenderer
              key={fieldKey}
              fieldId={fieldKey}
              config={fieldConfig}
              value={enrichedValues[fieldKey]}
              isEditing={editingFields.has(fieldKey)}
              isRequired={field.is_required}
              isSaving={savingFields.has(fieldKey)}
              onSave={(key, value) => handleFieldSave(key, value)}
              onStartEdit={() => handleStartEdit(fieldKey)}
              onStopEdit={() => handleStopEdit(fieldKey)}
            />
          );
        })}
      </div>

      {updateCustomFieldValue.error && (
        <div className="error-message">
          Error updating field: {updateCustomFieldValue.error.message}
        </div>
      )}

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: 'error' })}
      />
      
      {/* Render modal at top level */}
      <CostImpactModal 
        showModal={showCostImpactModal}
        onClose={() => setShowCostImpactModal(false)}
        costImpactConfig={costImpactConfig}
        formatCurrencyDisplay={formatCurrencyDisplay}
        COST_IMPACT_LABELS={COST_IMPACT_LABELS}
        projectId={projectId}
      />
    </div>
  );
};

export default CustomFieldsRenderer;